/**
 * Prescription templates: what one stores, and how applying one fills a prescription.
 *
 * A template keeps its medicines and lab tests as JSON text (medicinesJson, labTestsJson). Each
 * stored medicine is replayed later, one at a time, through POST /prescriptions/{id}/medicines,
 * so it must hold everything that endpoint reads, or the applied line is not what was prescribed.
 * Templates saved before this module lost the per-dose amount of a frequency line ("2 tablets
 * twice daily" came back as "twice daily", billed at one dose a day) and the days between doses
 * of an alternate-day line (which the server then refused). Old templates must still load and
 * apply, so every field is optional on the way back in.
 *
 * A template keeps its medicines whatever happens to the stock, and is applied without going
 * through the medicine search. So the server refuses a line the search would refuse (out of stock,
 * expired, no longer carried) and this module says which and why; low stock is advice, worked out
 * here from the same rows the search reads.
 */

import { stockStateFor } from './stockState';

// Every medicine field the writer's add-medicine payload sends (AddMedicineModal: formData plus
// its mode fields), which is exactly what the server's addMedicine reads. isFromInventory is kept
// because template edit mode shows a line as "External" without it; the server ignores it.
const MEDICINE_FIELDS = [
  'medicineName', 'medicineType', 'strength',
  'doseMorning', 'doseAfternoon', 'doseEvening', 'doseNight',
  'doseAmount', 'doseUnit',
  'frequency', 'frequencyIntervalDays',
  'timing', 'durationValue', 'durationUnit',
  'specialInstructions', 'specialInstructionsUrdu',
  // An injection course given here one dose per visit, or take-home. Absent on older templates,
  // and the server then gives an injection course here by default.
  'givenAtClinic',
];

const LAB_TEST_FIELDS = ['testName', 'instructions', 'instructionsUrdu', 'urgency'];

const SLOT_FIELDS = ['doseMorning', 'doseAfternoon', 'doseEvening', 'doseNight'];

const isObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

// Copies the listed fields. A field the source does not have stays absent rather than becoming
// null: absent means "never known" (an old template, or a line loaded from one), and the server
// then applies its own default, exactly as it did before.
const pick = (source, fields) => {
  const out = {};
  fields.forEach((f) => {
    if (source[f] !== undefined) out[f] = source[f];
  });
  return out;
};

// The inventory item a line is tied to. A saved prescription line carries it nested as
// medicine.id; a template line, and a line edited in template mode, as inventoryMedicineId.
export const inventoryIdOf = (line) => line?.medicine?.id ?? line?.inventoryMedicineId ?? null;

/**
 * Whether a medicine is already on the prescription, the same test the medicine picker applies.
 * Matched on the inventory id when both sides have one, because a repackaged liquid can exist as
 * two records with the same name and different bottle sizes (and two strengths of one medicine can
 * share a name); otherwise on the name, which is all an external medicine has.
 */
export const medicineAlreadyOn = (lines, name, inventoryId = null) =>
  (Array.isArray(lines) ? lines : []).some((m) => {
    if (!m) return false;
    const rowId = inventoryIdOf(m);
    if (inventoryId && rowId) return String(rowId) === String(inventoryId);
    return (m.medicineName || '').trim().toLowerCase() === (name || '').trim().toLowerCase();
  });

const labTestAlreadyOn = (tests, name) =>
  (Array.isArray(tests) ? tests : []).some(
    (t) => t && (t.testName || '').trim().toLowerCase() === (name || '').trim().toLowerCase()
  );

/** How a medicine line is named to the doctor: "Panadol 500mg". */
export const medicineLabel = (m) => {
  const name = String(m?.medicineName || '').trim();
  const strength = String(m?.strength || '').trim();
  if (!name) return 'A medicine';
  return strength && !name.toLowerCase().includes(strength.toLowerCase()) ? `${name} ${strength}` : name;
};

/**
 * One medicine as a template stores it: the whole of its dosing, whichever way it was written
 * (by time of day, by frequency with an amount per dose, every N days, liquid by ml or spoon,
 * continuous), plus its inventory link. Accepts a saved prescription line or a template line.
 */
export const templateMedicineLine = (m) => {
  const src = isObject(m) ? m : {};
  const inventoryMedicineId = inventoryIdOf(src);
  return {
    ...pick(src, MEDICINE_FIELDS),
    // Keeps the medicine recognised as a clinic-stocked item, not "External", when the template
    // is loaded or applied later.
    inventoryMedicineId,
    isFromInventory: src.isFromInventory ?? (inventoryMedicineId != null),
  };
};

/** One lab test as a template stores it, including its Urdu instructions. */
export const templateLabTestLine = (t) => pick(isObject(t) ? t : {}, LAB_TEST_FIELDS);

// The order the lines were written in. A saved prescription arrives ordered by displayOrder
// already; sorting again only when every line has one leaves lines being edited in template mode,
// which have none, in the order they are shown.
const inWrittenOrder = (lines) => {
  const list = (Array.isArray(lines) ? lines : []).filter(isObject);
  if (!list.every((l) => Number.isFinite(l.displayOrder))) return list;
  return list
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (a.l.displayOrder - b.l.displayOrder) || (a.i - b.i))
    .map((x) => x.l);
};

/**
 * Everything a template captures from the prescription being written, as the fields the template
 * endpoint takes. Both save paths (Save Template, and saving from the template editor or the
 * Complete & Print prompt) build their payload from this, so they cannot drift apart again.
 * The caller adds id, doctorId, templateName and description.
 */
export const templateContent = (formData, prescription) => {
  const fd = formData || {};
  return {
    diagnosis: fd.diagnosis,
    complaintsJson: JSON.stringify(fd.complaints || []),
    clinicalNotes: fd.clinicalNotes,
    examination: fd.examination,
    consultationOnly: !!fd.consultationOnly,
    followupDays: fd.followupDays ? parseInt(fd.followupDays, 10) : null,
    medicinesJson: JSON.stringify(inWrittenOrder(prescription?.medicines).map(templateMedicineLine)),
    labTestsJson: JSON.stringify(inWrittenOrder(prescription?.labTests).map(templateLabTestLine)),
    instructionsJson: JSON.stringify(fd.selectedInstructions || []),
  };
};

const parseLines = (json) => {
  if (!json) return [];
  try {
    const v = typeof json === 'string' ? JSON.parse(json) : json;
    return Array.isArray(v) ? v.filter(isObject) : [];
  } catch (e) {
    return [];
  }
};

/**
 * Why a stored medicine's dose needs the doctor's eye once applied, or null. A line written by
 * frequency with no amount per dose prints with no amount and is billed at one dose a day.
 * OLD_TEMPLATE: the template predates amounts being kept, so the amount was lost on saving.
 * NO_AMOUNT: the template kept the line as it was, and it had no amount then either.
 */
export const doseCheckFor = (stored) => {
  if (!isObject(stored) || !stored.frequency) return null;
  if (SLOT_FIELDS.some((f) => Number(stored[f]) > 0)) return null;
  if (!('doseAmount' in stored)) return 'OLD_TEMPLATE';
  return Number(stored.doseAmount) > 0 ? null : 'NO_AMOUNT';
};

/**
 * Advice on a stocked medicine's stock, from its inventory row, or null. Out of stock is not
 * advice: the server refuses that line, and says so.
 * LOW_STOCK: little left over today's prescriptions. RESERVED_TODAY: they call for all of it.
 */
export const stockAdviceFor = (row) => {
  if (!isObject(row)) return null;
  const { isReserved, isLow } = stockStateFor(row);
  if (isReserved) return 'RESERVED_TODAY';
  return isLow ? 'LOW_STOCK' : null;
};

/**
 * What applying a template to this prescription will do, line by line, before anything is sent.
 * Medicines and lab tests keep the template's order. A line already on the prescription (or
 * earlier in the same template) is marked ALREADY_ADDED and not sent again, using the picker's own
 * duplicate test, so two strengths of one medicine are both added.
 *
 * `stock` is the inventory rows the medicine search is served (medicineAPI.getAllWithInventory),
 * for the advice on low stock. Without it the template applies with no advice.
 */
export const planTemplateApply = (template, prescription, { stock = null } = {}) => {
  const stockById = new Map(
    (Array.isArray(stock) ? stock : []).filter(isObject).map((row) => [String(row.id), row])
  );
  const medicines = [];
  const onRx = [...(Array.isArray(prescription?.medicines) ? prescription.medicines : [])];
  parseLines(template?.medicinesJson).forEach((stored) => {
    const payload = templateMedicineLine(stored);
    // Stored templates can carry keys the server does not read; only the inventory link and the
    // dosing go out, and isFromInventory is decided by the server from the link.
    delete payload.isFromInventory;
    const label = medicineLabel(stored);
    const duplicate = medicineAlreadyOn(onRx, stored.medicineName, payload.inventoryMedicineId);
    const inventoryId = payload.inventoryMedicineId;
    medicines.push({
      label,
      payload,
      skip: duplicate ? 'ALREADY_ADDED' : null,
      doseCheck: duplicate ? null : doseCheckFor(stored),
      stockAdvice: duplicate || inventoryId == null ? null : stockAdviceFor(stockById.get(String(inventoryId))),
    });
    if (!duplicate) onRx.push({ medicineName: stored.medicineName, inventoryMedicineId: payload.inventoryMedicineId });
  });

  const labTests = [];
  const testsOnRx = [...(Array.isArray(prescription?.labTests) ? prescription.labTests : [])];
  parseLines(template?.labTestsJson).forEach((stored) => {
    const payload = templateLabTestLine(stored);
    const label = String(stored.testName || '').trim() || 'A lab test';
    const duplicate = labTestAlreadyOn(testsOnRx, stored.testName);
    labTests.push({ label, payload, skip: duplicate ? 'ALREADY_ADDED' : null });
    if (!duplicate) testsOnRx.push({ testName: stored.testName });
  });

  return { medicines, labTests };
};

/**
 * Sends a plan's lines to the server, one at a time and in order (the server numbers each line
 * one past the highest so far, so parallel calls would scramble the order). A line the server
 * refuses is recorded with its message and the rest carry on.
 *
 * `api` is { addMedicine(rxId, line), addLabTest(rxId, test) }, each resolving to the whole
 * prescription. Resolves to { added, failed, latest }: the lines added, the lines refused as
 * { kind, label, message }, and the last prescription the server sent back (null if none).
 */
export const runTemplateApply = async (plan, prescriptionId, api) => {
  const added = [];
  const failed = [];
  let latest = null;
  const send = async (kind, line, call) => {
    if (line.skip) return;
    try {
      const rx = await call(prescriptionId, line.payload);
      if (isObject(rx) && rx.id) latest = rx;
      added.push({
        kind,
        label: line.label,
        doseCheck: line.doseCheck || null,
        stockAdvice: line.stockAdvice || null,
      });
    } catch (err) {
      failed.push({ kind, label: line.label, message: String(err?.message || '').trim() });
    }
  };
  for (const line of plan?.medicines || []) {
    await send('medicine', line, api.addMedicine);
  }
  for (const line of plan?.labTests || []) {
    await send('labTest', line, api.addLabTest);
  }
  return { added, failed, latest };
};

const listNames = (names) => {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const withStop = (s) => (/[.!?]$/.test(s) ? s : `${s}.`);

/**
 * What the doctor is told once a template is applied: a headline, the lines to look at, and
 * whether it needs to stay on screen until dismissed. Plain sentences, one per problem line.
 *
 * A dose to check is corrected on this prescription only; the template keeps its line as it was,
 * so the same warning would come back on every use. The doctor is told where to fix the template:
 * the Templates tab, or Save Template on the last step when the clinic allows saving from a
 * prescription (`canSaveTemplateHere`; that button offers to update the template just loaded).
 */
export const templateApplyReport = (templateName, plan, outcome, { canSaveTemplateHere = false } = {}) => {
  const name = String(templateName || '').trim();
  const lines = [];
  (outcome?.failed || []).forEach((f) => {
    lines.push(`${f.label} was not added. ${withStop(f.message || 'The server did not say why, so add it by hand')}`);
  });
  (outcome?.added || []).forEach((a) => {
    if (a.stockAdvice === 'LOW_STOCK') {
      lines.push(`${a.label} is low on stock. Check there is enough for the course.`);
    } else if (a.stockAdvice === 'RESERVED_TODAY') {
      lines.push(`${a.label} may run out before this patient pays: today's earlier prescriptions call for what is on the shelf.`);
    }
  });
  let dosesToCheck = 0;
  (outcome?.added || []).forEach((a) => {
    if (a.doseCheck === 'OLD_TEMPLATE') {
      lines.push(`Check the dose of ${a.label}: this template was saved before doses were kept.`);
      dosesToCheck += 1;
    } else if (a.doseCheck === 'NO_AMOUNT') {
      lines.push(`Check the dose of ${a.label}: the template has no amount for each dose.`);
      dosesToCheck += 1;
    }
  });
  if (dosesToCheck > 0) {
    const where = canSaveTemplateHere ? 'Save Template on the last step, or the Templates tab' : 'the Templates tab';
    const what = dosesToCheck === 1 ? 'this dose' : 'these doses';
    lines.push(`Fix ${what} in the template too (${where}), or this warning will come back each time it is used.`);
  }
  const needsAttention = lines.length > 0;
  const duplicates = [...(plan?.medicines || []), ...(plan?.labTests || [])]
    .filter((l) => l.skip === 'ALREADY_ADDED')
    .map((l) => l.label);
  if (duplicates.length > 0) {
    lines.push(`Already on this prescription, so not added again: ${listNames(duplicates)}.`);
  }
  const applied = name ? `Template "${name}" applied` : 'Template applied';
  return {
    tone: needsAttention ? 'warning' : 'success',
    headline: needsAttention ? `${applied}. Please check:` : `${applied}.`,
    lines,
    // A problem stays until the doctor closes it; a clean apply fades like any other toast.
    sticky: needsAttention,
  };
};
